import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * usePdfDocument — Shared pdfjs document singleton.
 *
 * This hook loads the PDF document ONCE via pdfjs-dist and exposes a
 * `getPage(pageNumber)` getter. All consumers (main viewer + every thumbnail)
 * share the same document object. pdfjs caches decoded pages internally.
 *
 * STREAMING:
 * Here pass `disableAutoFetch: true` so pdfjs only downloads bytes on demand
 * (HTTP Range requests). The full PDF is never loaded into RAM upfront.
 *
 * @param {string} pdfUrl - The URL of the PDF to load (from the server)
 * @returns {{ pdfDoc, numPages, getPage, isLoading, error }}
 */
export function usePdfDocument(pdfUrl) {
  const [pdfDoc, setPdfDoc] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Keep a reference to the current task so we can cancel it on URL change
  const loadingTaskRef = useRef(null);

  useEffect(() => {
    if (!pdfUrl) return;

    setIsLoading(true);
    setError(null);
    setPdfDoc(null);
    setNumPages(0);

    // Lazy-load pdfjs-dist to avoid bundling it upfront
    import('pdfjs-dist/build/pdf').then((pdfjsLib) => {
      if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
        pdfjsLib.GlobalWorkerOptions.workerSrc =
          `https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`;
      }

      // Cancel any in-flight load from a previous URL
      if (loadingTaskRef.current) {
        loadingTaskRef.current.destroy();
      }

      const task = pdfjsLib.getDocument({
        url: pdfUrl,
         httpHeaders: {
          "ngrok-skip-browser-warning": "69420"
        },

        // Streaming: only fetch bytes as pages are requested
        disableStream: false,
        disableAutoFetch: true,
        rangeChunkSize: 65536, // 64 KB per chunk
      });

      loadingTaskRef.current = task;

      task.promise.then((doc) => {
        setPdfDoc(doc);
        setNumPages(doc.numPages);
        setIsLoading(false);
      }).catch((err) => {
        // Ignore cancellation errors (triggered intentionally on url change)
        if (err?.name !== 'RenderingCancelledException' && err?.message !== 'Loading PDF cancelled') {
          console.error('[usePdfDocument] Failed to load PDF:', err);
          setError(err);
          setIsLoading(false);
        }
      });
    }).catch((err) => {
      console.error('[usePdfDocument] Failed to import pdfjs-dist:', err);
      setError(err);
      setIsLoading(false);
    });

    return () => {
      // Cleanup: cancel the loading task when the component unmounts or url changes
      if (loadingTaskRef.current) {
        loadingTaskRef.current.destroy();
        loadingTaskRef.current = null;
      }
    };
  }, [pdfUrl]);

  /**
   * getPage — Lazily fetch a single page from the shared document.
   * pdfjs caches decoded pages internally; calling this multiple times
   * for the same page number is safe and cheap.
   *
   * @param {number} pageNumber - 1-indexed page number
   * @returns {Promise<PDFPageProxy>}
   */
  const getPage = useCallback((pageNumber) => {
    if (!pdfDoc) return Promise.reject(new Error('PDF not loaded'));
    return pdfDoc.getPage(pageNumber);
  }, [pdfDoc]);

  return { pdfDoc, numPages, getPage, isLoading, error };
}
